import {
  Component,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  EventEmitter,
  type OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ContentChildren,
  QueryList,
  ChangeDetectionStrategy
} from '@angular/core';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { InvoiceRow } from '../../model/invoice-row';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { Command, CommandBase } from '../../../core/model/command';
import { AllowSort } from '../../../core/service/factory/allow-sort';
import { InvoiceRowFormDefaultsModel } from '../../model/invoice-row-edit';
import { Subscription } from 'rxjs';
import { InvoiceRowService } from '../../service/data/invoice-row-service';
import { CodingDefaultValuesConfig } from '../../model/coding';
import { InvoiceRowFormComponent, InvoiceRowFormContext } from '../invoice-row-form/invoice-row-form.component';
import { Money } from '../../../core/model/money';
import { InvoiceAmountService } from '../../service/invoice-amount.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { defaultDimensionLabels } from '../../../accounting/model/dimension-labels';

export interface InvoiceRowListContext {
  id: string;
  companyId: string;
  allowEditInvoiceRows: boolean;
  amountAndTextRows: InvoiceRow[];
  currencyCode: string;
  codingDefaultValues: CodingDefaultValuesConfig;
}

@Component({
    selector: 'app-invoice-row-list',
    templateUrl: './invoice-row-list.component.html',
    styleUrls: ['./invoice-row-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InvoiceRowListComponent extends CommandController implements OnInit, OnDestroy {
  editInvoiceRowCommandName = 'editInvoiceRow';
  deleteInvoiceRowCommandName = 'deleteInvoiceRow';

  private _initSub: Subscription = new Subscription();

  isInitWorking = false;

  private _dataContext: InvoiceRowListContext;
  itemsSource: InvoiceRow[] = [];
  defaults: InvoiceRowFormDefaultsModel;

  private _filterFormSub: Subscription = new Subscription();
  filterFormGroup: FormGroup;

  @Input() set dataContext(context: InvoiceRowListContext) {
    this._initSub?.unsubscribe();

    this._dataContext = context;

    let showInfoRows = true;
    if (!this.filterFormGroup) {
      let form = this._fb.group({
        showInformationRow: this._fc.create('INVOICE_ROW.SHOW_INFORMATION_ROWS', this._dataContext.allowEditInvoiceRows),
      });

      this._filterFormSub.add(
        form.controls['showInformationRow'].valueChanges.subscribe(val => {
          this.setItemSource(this.dataContext?.amountAndTextRows, val);
        })
      );

      this.filterFormGroup = form;
      showInfoRows = this._dataContext.allowEditInvoiceRows;
    } else {
      showInfoRows = this.filterFormGroup.controls['showInformationRow'].value;
    }

    this.setItemSource(this.dataContext?.amountAndTextRows, showInfoRows);
    this.isInitWorking = true;
    this.updateRows();
    this._initSub = this.rowService.getDefaultsViewModel(context.companyId).subscribe({
      next: val => {
        this.defaults = val;
        const labels = this._dimensionLabelsService.fromCodingConfig(val?.codingConfig);
        this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'codingCostPlace', labels.costPlaceLabel);
        this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'codingCostBearer', labels.costBearerLabel);
        this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'codingCounterpart', labels.counterpartLabel);
        this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'codingFunction', labels.functionLabel);
        this._dimensionLabelsService.setColumnCaption(this.columnDefinition, 'codingProject', labels.projectLabel);
        this.columnDefinition = [...this.columnDefinition];
      },
      complete: () => {
        this.isInitWorking = false;
      },
    });
  }

  setItemSource(itemsSource: InvoiceRow[], showInfoRows: boolean) {
    this.itemsSource = showInfoRows
      ? this.dataContext?.amountAndTextRows
      : this.dataContext?.amountAndTextRows.filter(r => !r.isInformation);
  }

  get dataContext(): InvoiceRowListContext {
    return this._dataContext;
  }

  @Output() invoiceRowCreate = new EventEmitter<InvoiceRow>();
  @Output() invoiceRowChange = new EventEmitter<InvoiceRow>();
  @Output() invoiceRowDelete = new EventEmitter<string>();
  @Output() invoiceRowsReorder = new EventEmitter<void>();

  @ViewChild('rowEditSidebar', { static: false }) rowEditSidebar: TemplateRef<any>;
  @ViewChild('rowForm') rowForm: InvoiceRowFormComponent;

  columnDefinition: ColumnDefinition[];
  menuDefinition: MenuItemDefinition[];

  constructor(
    dispatcher: CommandDispatcher,
    factory: ColumnDefinitionFactory,
    private sidebarService: SidebarService,
    private rowService: InvoiceRowService,
    private invoiceAmountService: InvoiceAmountService,
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private readonly _dimensionLabelsService: DimensionLabelsService
  ) {
    super(dispatcher, null);

    this.columnDefinition = [
      factory.create('INVOICE_ROW.DESCRIPTION', 'description', { allowSort: AllowSort.No }),
      factory.create('LEASE_AGREEMENT_ROW.ARTICLE', 'articleCode', { allowSort: AllowSort.No }),
      factory.createMoneyOptional('INVOICE_ROW.AMOUNT', 'amount', { allowSort: AllowSort.No }),
      factory.createMoneyOptional('INVOICE.PREVIEW.VAT', 'vatAmount', { allowSort: AllowSort.No }),
      factory.createMoneyOptional('INVOICE_ROW.AMOUNT_INC_VAT', 'amountIncludingVat', { allowSort: AllowSort.No }),
      factory.create('COMMON.LEASE_AGREEMENT', 'leaseAgreementNumber', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
      factory.create(defaultDimensionLabels.costBearerLabel, 'codingCostBearer', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      factory.create(defaultDimensionLabels.costPlaceLabel, 'codingCostPlace', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      factory.create(defaultDimensionLabels.counterpartLabel, 'codingCounterpart', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      factory.create(defaultDimensionLabels.functionLabel, 'codingFunction', {
        allowSort: AllowSort.No,
        visibility: ColumnVisibility.optional,
      }),
      factory.create(defaultDimensionLabels.projectLabel, 'codingProject', { allowSort: AllowSort.No, visibility: ColumnVisibility.optional }),
    ];

    this.menuDefinition = [
      new MenuItemDefinition('EDIT_BTN', this.editInvoiceRowCommandName),
      new MenuItemDefinition('DELETE_BTN', this.deleteInvoiceRowCommandName),
    ];
  }

  get canEdit() {
    return this.dataContext.allowEditInvoiceRows && !this.isInitWorking;
  }

  canEditRow(item: InvoiceRow) {
    return !item.isRounding && !this.isInitWorking;
  }

  canExecute(command: CommandBase): boolean {
    if (command instanceof Command) {
      switch (command.id) {
        case this.editInvoiceRowCommandName:
          return this.canEdit && this.canEditRow(command.context);

        case this.deleteInvoiceRowCommandName:
          return this.canEdit && this.canEditRow(command.context);

        default:
          return super.canExecute(command);
      }
    }
  }

  execute(command: CommandBase): void {
    if (command instanceof Command) {
      switch (command.id) {
        case this.editInvoiceRowCommandName:
          this.editRow(command.context);
          break;

        case this.deleteInvoiceRowCommandName:
          this.deleteRowCommand(command.context);
          break;
      }
    }
  }

  formContext: InvoiceRowFormContext;

  createRowFormContext(row, isNew: boolean): InvoiceRowFormContext {
    return {
      invoiceRow: row,
      isNew: isNew,
      isReadonly: !this.dataContext.allowEditInvoiceRows || !this.canEditRow(row),
      defaults: this.defaults,
      codingDefaultValues: this.getCodingDefaultValues(),
    };
  }

  private getCodingDefaultValues(): CodingDefaultValuesConfig {
    // First row we base on the default values that comes from 'outside' (passed by data context).
    // After that - based on existing rows.
    // If existing invoice rows have identical coding values - we can 'suggest' them as default for future ones.
    if (this.dataContext.amountAndTextRows.length == 0) {
      return this.dataContext.codingDefaultValues;
    }

    return {
      costPlace: this.getDefaultCodeValueByRows(ir => ir.coding?.costPlace),
      costBearer: this.getDefaultCodeValueByRows(ir => ir.coding?.costBearer),
      counterpart: this.getDefaultCodeValueByRows(ir => ir.coding?.counterpart),
      function: this.getDefaultCodeValueByRows(ir => ir.coding?.function),
      project: this.getDefaultCodeValueByRows(ir => ir.coding?.project),
    };
  }

  private getCodingGroupByPart(codePartFunc: (row: InvoiceRow) => string): Record<string, InvoiceRow[]> {
    return this.dataContext.amountAndTextRows.reduce(
      (acc, item) => {
        let codePart = codePartFunc(item);
        if (codePart) {
          if (!acc[codePart]) {
            acc[codePart] = [];
          }
          acc[codePart].push(item);
        }
        return acc;
      },
      {} as Record<string, InvoiceRow[]>
    );
  }

  private getDefaultCodeValueByRows(codePartFunc: (row: InvoiceRow) => string) {
    const keys = Object.keys(this.getCodingGroupByPart(codePartFunc));

    // If all the rows contain the same code part - we suggest it by default.
    if (keys.length === 1 && keys[0]) {
      return keys[0];
    }

    // If there's more than single one - empty by default.
    return null;
  }

  onInvoiceRowClicked(e: { context: InvoiceRow; event: any }): void {
    if (this.isInitWorking) return;

    this.editRow(e.context);
  }

  editRow(row: InvoiceRow) {
    this.formContext = this.createRowFormContext(row, false);
    this.sidebarService.setSidebarContent({
      template: this.rowEditSidebar,
      onClose: () => {
        this.onSidebarClose();
      },
    });
    this.sidebarService.toggleSidebar(true);
  }

  addInvoiceRow() {
    let newRow = { id: null, invoiceId: this.dataContext.id, isInformation: false };
    this.openNewRowSidebar(newRow);
  }

  addInfoRow() {
    let newRow = { id: null, invoiceId: this.dataContext.id, isInformation: true };
    this.openNewRowSidebar(newRow);
  }

  addFavorite(articleId: string) {
    let newRow = { id: null, invoiceId: this.dataContext.id, isInformation: false, articleId: articleId };
    this.openNewRowSidebar(newRow);
  }

  private openNewRowSidebar(invoiceRow) {
    this.formContext = this.createRowFormContext(invoiceRow, true);
    this.sidebarService.setSidebarContent({
      template: this.rowEditSidebar,
      onClose: () => {
        this.onSidebarClose();
      },
    });
    this.sidebarService.toggleSidebar(true);
  }

  invoiceRowCreated(row: InvoiceRow) {
    // wrap it into a new model to trigger properties evaluation.
    this.dataContext.amountAndTextRows.push(new InvoiceRow(row));
    this.updateAfterInvoiceRowChange();
    this.invoiceRowCreate.emit(row);
    this.sidebarService.toggleSidebar(false);
  }

  invoiceRowChanged(row: InvoiceRow) {
    this.updateAfterInvoiceRowChange();
    this.invoiceRowChange.emit(row);
    this.sidebarService.toggleSidebar(false);
  }

  invoiceRowEditCanceled() {
    this.sidebarService.toggleSidebar(false);
  }

  invoiceRowDeleted(row: InvoiceRow) {
    this.deleteRow(row);
    this.updateAfterInvoiceRowChange();
    this.invoiceRowDelete.emit(row.id);
    this.sidebarService.toggleSidebar(false);
  }

  deleteRow(row: InvoiceRow) {
    this.dataContext.amountAndTextRows.splice(this.dataContext.amountAndTextRows.indexOf(row), 1);
  }

  deleteRowCommand(row: InvoiceRow) {
    this.deleteRow(row);
    this.invoiceRowDelete.emit(row.id);
  }

  // app-drag-drop-list-view emits CdkDragDrop<TSource> where TSource is the item type (InvoiceRow).
  drop(event: CdkDragDrop<InvoiceRow>) {
    this.invoiceRowsReorder.emit();
  }

  getDragPreviewText(invoiceRow: InvoiceRow) {
    return invoiceRow.description;
  }

  get favoriteArticles() {
    return this.defaults?.articleSelection?.filter(a => a.favorite);
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this._initSub?.unsubscribe();
    this._filterFormSub?.unsubscribe();
  }

  onSidebarClose() {
    this.rowForm?.cleanupForm();
    this.formContext = null;
  }

  updateAfterInvoiceRowChange() {
    this.updateCodingDefaultValues();
    this.updateRows();
  }

  updateCodingDefaultValues() {
    if (this.formContext) {
      this.formContext.codingDefaultValues = this.getCodingDefaultValues();
    }
  }

  rounding: Money = null;
  invoiceTotal: Money = null;

  private getRoundingRow() {
    return this.dataContext.amountAndTextRows.find(r => r.isRounding);
  }

  private setRoundingValue(calculatedRounding: Money) {
    if (this.invoiceTotal.IsNegative) {
      var roundingRow = this.getRoundingRow();
      if (roundingRow) {
        roundingRow.amount = calculatedRounding;
        roundingRow.amountIncludingVat = calculatedRounding;
      }
    }

    this.rounding = calculatedRounding;
  }

  private updateRows() {
    let rows = this.dataContext.amountAndTextRows;

    // If there's an existing rounding row - we want to recalculate its value.
    // Put 0 on existing rounding row, recalculate and then put in the new value.
    // Also move it to the end of the list in case new rows were added.
    var roundingRow = this.getRoundingRow();
    if (roundingRow) {
      roundingRow.amount = new Money(this.dataContext.currencyCode, 0);
      roundingRow.amountIncludingVat = new Money(this.dataContext.currencyCode, 0);
      roundingRow.vatAmount = new Money(this.dataContext.currencyCode, 0);

      rows.push(rows.splice(rows.indexOf(roundingRow), 1)[0]);
    }

    let amounts = this.invoiceAmountService.calculateAmounts(rows, this.dataContext.currencyCode);

    this.invoiceTotal = amounts.amount;

    // here we set the rounding value to a new one, including rounding row if it exists.
    this.setRoundingValue(amounts.rounding);
  }
}
